/**
 * Resolve the QR token in the URL into a usable {tenantId, tableId,
 * branchId, tableCode, tableName} tuple.
 *
 * Backend contract (pending — tracked as §28 in BACKEND_AGENT_PROMPTS):
 *   GET /api/restaurant/RestaurantTable/by-qr/{qrToken}
 *     → { tenantId, branchId, restaurantTableId, code, name,
 *         tenantName, tenantLogoUrl }
 *
 * Fallback (works today against the existing CRUD endpoint):
 *   GET /api/restaurant/RestaurantTable?qrToken=<token>
 *   → unwrap first match.
 *
 * This hook tries the dedicated endpoint first; on 404 it falls back
 * to the filter query so the screen works before the backend ships
 * the dedicated route.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { apiClient, restaurantTableService } from '@kss/api';
import { setTable } from './dineSlice';
import type { DineRootState } from './dineSlice';
import type { TableContext } from './types';

type Status = 'idle' | 'loading' | 'ready' | 'error';

export function useTableContext(): {
  status: Status;
  table: TableContext | null;
  error: string | null;
} {
  const { tenantCode = '', qrToken = '' } = useParams<{
    tenantCode: string;
    qrToken: string;
  }>();
  const dispatch = useDispatch();
  const table = useSelector((s: DineRootState) => s.dine.table);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantCode || !qrToken) {
      setStatus('error');
      setError('Invalid QR link.');
      return;
    }
    if (table && table.qrToken === qrToken && table.tenantCode === tenantCode && table.tableId) {
      setStatus('ready');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    setError(null);

    (async () => {
      let resolved: TableContext | null = null;
      // 1. Try the dedicated endpoint (cheap, single result, no auth).
      try {
        const resp = await apiClient.get(
          `/restaurant/RestaurantTable/by-qr/${encodeURIComponent(qrToken)}`,
        );
        const d = (resp.data?.data ?? resp.data) as Record<string, unknown> | null;
        if (d && (d.restaurantTableId || d.tableId)) {
          resolved = {
            tenantCode,
            qrToken,
            tenantId: String(d.tenantId ?? ''),
            branchId: d.branchId ? String(d.branchId) : undefined,
            tableId: String(d.restaurantTableId ?? d.tableId ?? ''),
            tableCode: d.code ? String(d.code) : undefined,
            tableName: d.name ? String(d.name) : undefined,
            tenantName: d.tenantName ? String(d.tenantName) : undefined,
            tenantLogoUrl: d.tenantLogoUrl ? String(d.tenantLogoUrl) : undefined,
          };
        }
      } catch {
        // 404 or 405 → fall back to filter below.
      }

      // 2. Filter fallback — works on the live CRUD endpoint today.
      if (!resolved) {
        try {
          // `filter` returns matching rows; we can't search by qrToken
          // through the typed filter (it's not in the Pick<>), so we
          // pull the full list and match client-side. Acceptable
          // until §28 lands the dedicated route.
          //
          // The live backend currently drops the speculative `qrToken`
          // column, so we ALSO look for `qr=<uuid>` packed into the
          // `remarks` audit trail (TableQrCodes stamps it there).
          const rows = await restaurantTableService.all();
          const remarksHasToken = (r: { remarks?: string }) =>
            r.remarks ? new RegExp(`qr=${qrToken}\\b`).test(r.remarks) : false;
          const match = rows?.find(
            (r) => r.qrToken === qrToken || remarksHasToken(r),
          );
          if (match) {
            const m = match as typeof match & { id?: string | number };
            resolved = {
              tenantCode,
              qrToken,
              tenantId: String(match.tenantId ?? ''),
              branchId: match.branchId ? String(match.branchId) : undefined,
              tableId: String(match.restaurantTableId ?? m.id ?? ''),
              tableCode: match.code ? String(match.code) : undefined,
              tableName: match.name ? String(match.name) : undefined,
            };
          }
        } catch (e) {
          if (cancelled) return;
          setStatus('error');
          setError((e as Error)?.message ?? 'Could not load this table.');
          return;
        }
      }

      if (cancelled) return;
      if (!resolved?.tableId) {
        setStatus('error');
        setError('Table not found — please ask staff for a fresh QR code.');
        return;
      }
      dispatch(setTable(resolved));
      setStatus('ready');
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantCode, qrToken, dispatch, table?.qrToken, table?.tenantCode, table?.tableId]);

  return { status, table, error };
}
